// RBAC Selectors - Comprehensive collection of all RBAC selectors
import { createSelector } from 'reselect';

// Import all individual selectors
import {
  makeSelectScopes,
  makeSelectScopesList,
  makeSelectScopesLoading,
  makeSelectScopesError,
  makeSelectScopesLastUpdated,
  makeSelectScopeById,
  makeSelectScopesByCategory,
  makeSelectScopesByResource,
  makeSelectScopesByAction,
  makeSelectScopesSearch,
  makeSelectScopesGroupedByResource,
  makeSelectScopesGroupedByCategory,
} from './scopes/selector';

import {
  makeSelectRoleTemplates,
  makeSelectRoleTemplatesList,
  makeSelectRoleTemplatesLoading,
  makeSelectRoleTemplatesError,
  makeSelectRoleTemplatesLastUpdated,
  makeSelectRoleTemplateById,
  makeSelectRoleTemplatesByCategory,
  makeSelectRoleTemplatesByScope,
  makeSelectRoleTemplatesSearch,
  makeSelectRoleTemplatesGroupedByCategory,
  makeSelectRoleTemplatesWithScopeCount,
  makeSelectRoleTemplatesByScopeCount,
} from './roleTemplates/selector';

import {
  makeSelectPageRoles,
  makeSelectPageRolesList,
  makeSelectPageRolesLoading,
  makeSelectPageRolesError,
  makeSelectPageRolesLastUpdated,
  makeSelectPageRolesPagination,
  makeSelectPageRoleById,
  makeSelectPageRolesByPage,
  makeSelectPageRolesByTemplate,
  makeSelectPageRolesByScope,
  makeSelectPageRolesSearch,
  makeSelectPageRolesGroupedByPage,
  makeSelectPageRolesGroupedByTemplate,
  makeSelectPageRolesWithScopeCount,
  makeSelectPageRolesByScopeCount,
  makeSelectPageRolesByStatus,
  makeSelectPageRolesActive,
} from './pageRoles/selector';

import {
  makeSelectPageMemberships,
  makeSelectPageMembershipsList,
  makeSelectPageMembershipsLoading,
  makeSelectPageMembershipsError,
  makeSelectPageMembershipsLastUpdated,
  makeSelectPageMembershipsPagination,
  makeSelectPageMembershipById,
  makeSelectPageMembershipsByPage,
  makeSelectPageMembershipsByUser,
  makeSelectPageMembershipsByRole,
  makeSelectPageMembershipsByStatus,
  makeSelectPageMembershipsActive,
  makeSelectPageMembershipsPending,
  makeSelectPageMembershipsSearch,
  makeSelectPageMembershipsGroupedByPage,
  makeSelectPageMembershipsGroupedByRole,
  makeSelectPageMembershipsGroupedByStatus,
  makeSelectPageMembershipsByUserAndPage,
  makeSelectPageMembershipsCountByPage,
  makeSelectPageMembershipsCountByRole,
} from './pageMemberships/selector';

import {
  makeSelectApiKeys,
  makeSelectApiKeysList,
  makeSelectApiKeysLoading,
  makeSelectApiKeysError,
  makeSelectApiKeysLastUpdated,
  makeSelectApiKeysTotal,
  makeSelectApiKeyById,
  makeSelectApiKeysByPage,
  makeSelectApiKeysByRole,
  makeSelectApiKeysByStatus,
  makeSelectApiKeysActive,
  makeSelectApiKeysInactive,
  makeSelectApiKeysExpired,
  makeSelectApiKeysExpiringSoon,
  makeSelectApiKeysSearch,
  makeSelectApiKeysGroupedByPage,
  makeSelectApiKeysGroupedByRole,
  makeSelectApiKeysGroupedByStatus,
  makeSelectApiKeysByPageAndRole,
  makeSelectApiKeysCountByPage,
  makeSelectApiKeysCountByRole,
  makeSelectApiKeysWithMaskedKeys,
  makeSelectApiKeysByCreatedDateRange,
  makeSelectApiKeysByLastUsedDateRange,
} from './apiKeys/selector';

// Combined selectors for common use cases
const selectRBACDomain = state => ({
  scopes: state.Scopes,
  roleTemplates: state.RoleTemplates,
  pageRoles: state.PageRoles,
  pageMemberships: state.PageMemberships,
  apiKeys: state.ApiKeys,
});

// Global RBAC state selector
const makeSelectRBAC = () =>
  createSelector(selectRBACDomain, substate => substate);

// Loading state for all RBAC entities
const makeSelectRBACLoading = () =>
  createSelector(selectRBACDomain, substate =>
    substate.scopes.loading ||
    substate.roleTemplates.loading ||
    substate.pageRoles.loading ||
    substate.pageMemberships.loading ||
    substate.apiKeys.loading
  );

// Error state for all RBAC entities
const makeSelectRBACError = () =>
  createSelector(selectRBACDomain, substate =>
    substate.scopes.error ||
    substate.roleTemplates.error ||
    substate.pageRoles.error ||
    substate.pageMemberships.error ||
    substate.apiKeys.error
  );

// Check if any RBAC data is available
const makeSelectRBACDataAvailable = () =>
  createSelector(selectRBACDomain, substate =>
    substate.scopes.scopes.length > 0 ||
    substate.roleTemplates.roleTemplates.length > 0 ||
    substate.pageRoles.pageRoles.length > 0 ||
    substate.pageMemberships.pageMemberships.length > 0 ||
    substate.apiKeys.apiKeys.length > 0
  );

// Get RBAC statistics
const makeSelectRBACStats = () =>
  createSelector(selectRBACDomain, substate => ({
    scopesCount: substate.scopes.scopes.length,
    roleTemplatesCount: substate.roleTemplates.roleTemplates.length,
    pageRolesCount: substate.pageRoles.pageRoles.length,
    pageMembershipsCount: substate.pageMemberships.pageMemberships.length,
    apiKeysCount: substate.apiKeys.apiKeys.length,
    totalApiKeys: substate.apiKeys.total,
  }));

// Export all selectors
export {
  // Scopes selectors
  makeSelectScopes,
  makeSelectScopesList,
  makeSelectScopesLoading,
  makeSelectScopesError,
  makeSelectScopesLastUpdated,
  makeSelectScopeById,
  makeSelectScopesByCategory,
  makeSelectScopesByResource,
  makeSelectScopesByAction,
  makeSelectScopesSearch,
  makeSelectScopesGroupedByResource,
  makeSelectScopesGroupedByCategory,

  // Role Templates selectors
  makeSelectRoleTemplates,
  makeSelectRoleTemplatesList,
  makeSelectRoleTemplatesLoading,
  makeSelectRoleTemplatesError,
  makeSelectRoleTemplatesLastUpdated,
  makeSelectRoleTemplateById,
  makeSelectRoleTemplatesByCategory,
  makeSelectRoleTemplatesByScope,
  makeSelectRoleTemplatesSearch,
  makeSelectRoleTemplatesGroupedByCategory,
  makeSelectRoleTemplatesWithScopeCount,
  makeSelectRoleTemplatesByScopeCount,

  // Page Roles selectors
  makeSelectPageRoles,
  makeSelectPageRolesList,
  makeSelectPageRolesLoading,
  makeSelectPageRolesError,
  makeSelectPageRolesLastUpdated,
  makeSelectPageRolesPagination,
  makeSelectPageRoleById,
  makeSelectPageRolesByPage,
  makeSelectPageRolesByTemplate,
  makeSelectPageRolesByScope,
  makeSelectPageRolesSearch,
  makeSelectPageRolesGroupedByPage,
  makeSelectPageRolesGroupedByTemplate,
  makeSelectPageRolesWithScopeCount,
  makeSelectPageRolesByScopeCount,
  makeSelectPageRolesByStatus,
  makeSelectPageRolesActive,

  // Page Memberships selectors
  makeSelectPageMemberships,
  makeSelectPageMembershipsList,
  makeSelectPageMembershipsLoading,
  makeSelectPageMembershipsError,
  makeSelectPageMembershipsLastUpdated,
  makeSelectPageMembershipsPagination,
  makeSelectPageMembershipById,
  makeSelectPageMembershipsByPage,
  makeSelectPageMembershipsByUser,
  makeSelectPageMembershipsByRole,
  makeSelectPageMembershipsByStatus,
  makeSelectPageMembershipsActive,
  makeSelectPageMembershipsPending,
  makeSelectPageMembershipsSearch,
  makeSelectPageMembershipsGroupedByPage,
  makeSelectPageMembershipsGroupedByRole,
  makeSelectPageMembershipsGroupedByStatus,
  makeSelectPageMembershipsByUserAndPage,
  makeSelectPageMembershipsCountByPage,
  makeSelectPageMembershipsCountByRole,

  // API Keys selectors
  makeSelectApiKeys,
  makeSelectApiKeysList,
  makeSelectApiKeysLoading,
  makeSelectApiKeysError,
  makeSelectApiKeysLastUpdated,
  makeSelectApiKeysTotal,
  makeSelectApiKeyById,
  makeSelectApiKeysByPage,
  makeSelectApiKeysByRole,
  makeSelectApiKeysByStatus,
  makeSelectApiKeysActive,
  makeSelectApiKeysInactive,
  makeSelectApiKeysExpired,
  makeSelectApiKeysExpiringSoon,
  makeSelectApiKeysSearch,
  makeSelectApiKeysGroupedByPage,
  makeSelectApiKeysGroupedByRole,
  makeSelectApiKeysGroupedByStatus,
  makeSelectApiKeysByPageAndRole,
  makeSelectApiKeysCountByPage,
  makeSelectApiKeysCountByRole,
  makeSelectApiKeysWithMaskedKeys,
  makeSelectApiKeysByCreatedDateRange,
  makeSelectApiKeysByLastUsedDateRange,

  // Combined RBAC selectors
  makeSelectRBAC,
  makeSelectRBACLoading,
  makeSelectRBACError,
  makeSelectRBACDataAvailable,
  makeSelectRBACStats,
};
